// src/components/support/forms/DoorPin.js
import React from 'react';
import { ChevronLeft, Key } from 'lucide-react';
import { useAtom } from 'jotai';
import { alertModal, appConstants, userstate } from '../../../atomstore/store';
import { useRequestProcessor } from '../../../requestProcessor';
import { resetDoorPin } from '../../../queryfunctions/queryfunctions';
import SpinnerLoading from '../../common/SpinnerLoading';
import { useNavigate } from 'react-router-dom';

const DEFAULT_DOOR_PIN_MESSAGE =
    'The generated PIN will expire after 15 days if not used.';

const DEFAULT_DOOR_PIN_REGENERATE_CONFIRM_MESSAGE =
    'This PIN is for entrance door access at your centre. Please enable it only if required. You may skip this step if not needed.';

const DEFAULT_NO_PIN_MESSAGE = 'No PIN generated yet';

const DoorPin = () => {
    const navigate = useNavigate();
    const [user] = useAtom(userstate);
    const [constants] = useAtom(appConstants);
    const [, setModal] = useAtom(alertModal);
    const { useMutationWrapper } = useRequestProcessor();
    const resetPin = useMutationWrapper('resetDoorPin', () => resetDoorPin());

    const hasPin = Boolean(user?.password);
    const pin = user?.password;

    const handleGeneratePin = (e) => {
        e.preventDefault();
        resetPin.mutate();
    };

    const handleRegeneratePin = (e) => {
        e.preventDefault();
        setModal((prev) => ({
            ...prev,
            state: true,
            type: 'confirm',
            isConfirm: true,
            message:
                constants?.doorPinRegenerateConfirmMessage ||
                DEFAULT_DOOR_PIN_REGENERATE_CONFIRM_MESSAGE,
            cancelText: 'Skip',
            onOkPressed: () => resetPin.mutate(),
            onCancelPressed: () => { },
        }));
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {resetPin.isLoading && <SpinnerLoading />}

            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2
                    className="text-lg font-bold text-gray-800 cursor-pointer"
                    onClick={() => navigate(-1)}
                >
                    Door PIN
                </h2>
            </div>

            <div className="p-4 space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                    <p className="text-sm text-blue-800">
                        {constants?.doorPinMessage || DEFAULT_DOOR_PIN_MESSAGE}
                    </p>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Key className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Current PIN</p>
                                {hasPin ? (
                                    <p className="text-lg font-semibold text-gray-800 tracking-widest">{pin}</p>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">
                                        {constants?.doorPinNoPinMessage || DEFAULT_NO_PIN_MESSAGE}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            className="bg-orange-400 px-4 py-2 text-white rounded-lg font-semibold active:scale-95 transition"
                            onClick={hasPin ? handleRegeneratePin : handleGeneratePin}
                        >
                            {hasPin ? 'Re-generate' : 'Generate PIN'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoorPin;
